/** READ THIS!! This sidebar is the base sidebar for all three applications. If you are editing this file
 * that is probably wrong. Edit with care.
 */

import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import type RouterService from '@ember/routing/router-service';
import { t } from 'ember-intl';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { PhCaretRight } from 'ember-phosphor-icons';

/**
 * Represents a group of items within a sidebar that can be toggled to show or hide its children.
 * This component maintains its open/closed state and toggles it upon clicking the group label.
 *
 * @example
 * <SidebarItemGroup @icon="banknotes" @label="Accounting">
 *   <SidebarItem @route="authenticated.accounting.invoices.index" @label="Invoices" />
 * </SidebarItemGroup>
 *
 * @template
 * Consists of a button that toggles the visibility of a nested list containing child components,
 * which are rendered via the `default` block slot.
 *
 * @interface
 * Args:
 *  - icon?: Icons - Optional icon to display alongside the label.
 *  - label: string - The label text for the toggle button.
 * Blocks:
 *  - default: Component[] - The child components to display when the group is expanded.
 */

interface SidebarItemGroupSignature {
  Args: {
    icon?: unknown;
    label: string;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLLIElement;
}

export class SidebarItemGroup extends Component<SidebarItemGroupSignature> {
  @tracked isOpen = true;

  toggleOpen = () => {
    this.isOpen = !this.isOpen;
  };

  <template>
    <li ...attributes>
      <button
        type="button"
        class="hover:bg-gray-50 hover:text-midnight tio-fg-inverse -mx-2 flex items-start w-full text-left rounded-md p-2 gap-x-1 text-sm font-semibold"
        aria-controls="sub-menu-1"
        aria-expanded="false"
        {{on "click" this.toggleOpen}}
      >
        {{#if @icon}}
          <@icon class="h-5 w-5 shrink-0 hover:text-midnight" />
        {{/if}}

        <p>{{@label}}</p>
        <PhCaretRight
          @size="16"
          class="ml-auto shrink-0 flex-none
            {{if this.isOpen 'rotate-90 text-gray-500' 'text-gray-400'}}"
        />
      </button>
      <ul
        class="{{unless this.isOpen 'hidden'}}
          app-sidebar-submenu [&_button.animation-appear]:!pl-6"
      >
        {{yield}}
      </ul>
    </li>
  </template>
}

/**
 * Represents a single item within a sidebar, which is a link that can optionally display an icon.
 *
 * @example
 * <SidebarItem @route="authenticated.people.index" @icon="team" @label="People" />
 *
 * @template
 * Renders as a list item containing a link, potentially with an icon, styled according to its
 * active or hover state. The `active` class styling is conditional based on Ember's `LinkTo`
 * component.
 *
 * @interface
 * Args:
 *  - route: string - The route to link to.
 *  - icon?: Icons - Optional icon to display alongside the label.
 *  - label: string - Text label for the link.
 *  - isSubmenu?: boolean - Optional flag to indicate if this item is part of a submenu (affects styling).
 */

interface SidebarItemSignature {
  Args: {
    route: string;
    icon?: unknown;
    label: string;
    isSubmenu?: boolean;
  };
  Element: HTMLButtonElement;
}

/**
 * Top-level component for rendering a sidebar navigation structure, containing various navigation items,
 * possibly grouped.
 *
 * @template
 * A structured list of `SidebarItem` and `SidebarItemGroup` components representing different navigation
 * paths and functionalities of an application.
 *
 * @example
 * <Sidebar>
 *   <SidebarItem @route="authenticated.dashboard.index" @label="Dashboard" />
 *   <SidebarItemGroup @label="cog" @label="Settings">
 *     <SidebarItem @route="settings.profile" @label="Profile" />
 *   </SidebarItemGroup>
 * </Sidebar>
 */

export class SidebarItem extends Component<SidebarItemSignature> {
  @service declare router: RouterService;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transitionToRoute = (route: any, event: Event) => {
    // Fallback for browsers that don't support this API:
    if (!document.startViewTransition) {
      this.router.transitionTo(route);
      return;
    }

    function setActiveItem(element: HTMLElement) {
      const allLinks = document.querySelectorAll('#nav');
      allLinks.forEach((link) => link.classList.remove('animation-appear'));
      const navElement = element.closest('#nav');
      if (navElement) {
        navElement.classList.add('animation-appear');
      }
    }
    // With a transition:
    document.startViewTransition(() => {
      setActiveItem(event.target as HTMLElement);
      this.router.transitionTo(route);
    });
  };

  <template>
    {{! TODO: Should element type be configurable?}}
    <li>
      <button
        type="button"
        id="nav"
        {{on "click" (fn this.transitionToRoute @route)}}
        class="w-full group -mx-2 flex gap-x-1 rounded-md p-2 text-sm font-semibold tio-fg-inverse text-left hover:bg-gray-50 hover:text-midnight"
        ...attributes
      >
        {{#if @icon}}
          <@icon
            class="[&.active]:text-midnight [&.active]:font-bold h-5 w-5 shrink-0 hover:text-midnight"
          />
        {{/if}}
        <p>
          {{@label}}
        </p>
      </button>
    </li>
  </template>
}

interface SidebarSignature {
  Blocks: {
    default: [];
  };
}

export const Sidebar: TOC<SidebarSignature> = <template>
  <div class="tio-row-flush h-16 shrink-0">
    <span class="font-semibold text-2xl mt-4 mb-2 tio-fg-inverse">
      {{t "common.sidebar.tio"}}
    </span>
  </div>
  <nav class="tio-col-flush flex-1">
    <ul role="list" class="flex flex-1 flex-col gap-y-7">
      {{yield}}
    </ul>
  </nav>
</template>;
